"use client";

/**
 * @file src/app/resume-check/phases/InputPhase.tsx
 *
 * Input phase for the /resume-check tool. Full-page focused form: resume (upload, paste, or a pre-made sample for a
 * role) and a required job description (pasted or generated from the resume). Analyze stays disabled until both exist.
 */

import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Upload, Loader2, FileText, Briefcase, Sparkles, Pencil } from "lucide-react";
import { trackEvent, clientLogger } from "@/lib/logger-client";
import { SAMPLE_ROLE_OPTIONS } from "@/lib/sampleProfiles";
import { getSampleResumeText } from "@/lib/utils/sampleResumeText";
import { looksLikeMarkdown } from "@/lib/utils/looksLikeMarkdown";

interface InputPhaseProps {
  initialResumeText?: string;
  initialJobDescription?: string;
  onSubmit: (resumeText: string, jobDescription: string) => void;
}

export function InputPhase({ initialResumeText = "", initialJobDescription = "", onSubmit }: InputPhaseProps) {
  const [resumeText, setResumeText] = useState(initialResumeText);
  const [jobDescription, setJobDescription] = useState(initialJobDescription);
  const [isDragging, setIsDragging] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasTrackedResumeFocus = useRef(false);
  const hasTrackedJobFocus = useRef(false);
  const [isGeneratingSampleJob, setIsGeneratingSampleJob] = useState(false);
  // Markdown job descriptions (for example generated ones) show rendered; Edit switches back to the text box.
  const [jobView, setJobView] = useState<"edit" | "preview">(
    looksLikeMarkdown(initialJobDescription) ? "preview" : "edit",
  );
  const jobTextareaRef = useRef<HTMLTextAreaElement>(null);
  const focusJobOnEdit = useRef(false);

  useEffect(() => {
    if (jobView === "edit" && focusJobOnEdit.current) {
      focusJobOnEdit.current = false;
      jobTextareaRef.current?.focus();
    }
  }, [jobView]);

  const handleSampleRole = (role: string) => {
    setResumeText(getSampleResumeText(role));
    setUploadError(null);
    setError(null);
    trackEvent("resume_check_sample_role_selected", { role });
  };

  const canAnalyze = !!resumeText.trim() && !!jobDescription.trim() && !isExtracting && !isGeneratingSampleJob;

  const handleGenerateSampleJob = async () => {
    if (!resumeText.trim()) return;
    setIsGeneratingSampleJob(true);
    setError(null);
    try {
      const res = await fetch("/api/landing/resume-check/generate-sample-job", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeText }),
      });
      if (!res.ok) throw new Error("Failed to generate sample job description");
      const data = await res.json();
      setJobDescription(data.jobDescription);
      setJobView(looksLikeMarkdown(data.jobDescription) ? "preview" : "edit");
      trackEvent("resume_check_sample_job_generated", {
        resume_length: resumeText.length,
      });
    } catch (err) {
      clientLogger.error("Failed to generate sample job", { error: err });
      setError("Could not generate a job. Paste one instead.");
    } finally {
      setIsGeneratingSampleJob(false);
    }
  };

  const extractFileText = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch("/api/landing/extract-docx", {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || "Failed to extract text from document");
    }
    const data = await res.json();
    return data.text;
  };

  const processFile = async (selectedFile: File) => {
    setIsExtracting(true);
    setUploadError(null);
    try {
      const isPDF = selectedFile.type === "application/pdf" || selectedFile.name.toLowerCase().endsWith(".pdf");
      const isDOCX = selectedFile.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || selectedFile.name.toLowerCase().endsWith(".docx");

      if (!isPDF && !isDOCX) {
        throw new Error("Unsupported file type. Please upload a PDF or DOCX file.");
      }

      const text = await extractFileText(selectedFile);
      setResumeText(text);
      trackEvent("resume_check_file_parsed", {
        file_type: selectedFile.type,
        character_count: text.length,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to process file";
      setUploadError(msg);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAnalyze) return;
    setError(null);
    trackEvent("resume_check_submitted", {
      resume_length: resumeText.length,
    });
    onSubmit(resumeText.trim(), jobDescription.trim());
  };

  return (
    <div className="min-h-[calc(100vh-4.5rem)] flex flex-col items-center justify-start py-16 px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-2xl"
      >
        {/* Headline */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight mb-4">
            Find out exactly why you&apos;re not getting the interview.
          </h1>
          <p className="text-lg text-muted-foreground max-w-lg mx-auto leading-relaxed">
            Your resume scored against the job. Free, no account needed.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Resume Upload Zone */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="resume-input" className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <FileText className="w-4 h-4 text-primary" />
                Your Resume
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-primary hover:text-primary/80 font-semibold transition-colors inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1"
                aria-label="Upload resume PDF or DOCX"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload PDF or DOCX
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) processFile(f);
                }}
                className="hidden"
              />
            </div>

            {/* Drop Zone */}
            <div
              className="relative"
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const f = e.dataTransfer.files[0];
                if (f) processFile(f);
              }}
            >
              <textarea
                id="resume-input"
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                onFocus={() => {
                  if (!hasTrackedResumeFocus.current) {
                    hasTrackedResumeFocus.current = true;
                    trackEvent("resume_check_resume_focused");
                  }
                }}
                placeholder="Paste your resume here, or drag and drop a PDF or DOCX file."
                className="input-thin w-full h-48 resize-none"
                disabled={isExtracting}
                aria-describedby="resume-hint"
              />

              {/* Drag overlay */}
              {isDragging && (
                <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex flex-col items-center justify-center pointer-events-none backdrop-blur-[1px]">
                  <Upload className="w-8 h-8 text-primary mb-2" />
                  <p className="text-sm font-semibold text-primary">Drop your resume here</p>
                </div>
              )}

              {/* Extracting overlay */}
              {isExtracting && (
                <div className="absolute inset-0 bg-surface/80 rounded-lg flex flex-col items-center justify-center pointer-events-none">
                  <Loader2 className="w-6 h-6 text-primary animate-spin mb-2" />
                  <p className="text-sm font-medium text-foreground">Extracting resume text...</p>
                </div>
              )}
            </div>

            {uploadError && (
              <p className="text-xs text-destructive mt-1.5 px-1">{uploadError}</p>
            )}
            <p id="resume-hint" className="text-xs text-muted-foreground mt-1.5 px-1">
              PDF and DOCX supported.
            </p>

            {/* No resume: pre-made sample for a role */}
            <div className="mt-3">
              <p className="text-xs font-semibold text-foreground mb-2 px-1">No resume? Try a sample</p>
              <div className="flex flex-wrap gap-2">
                {SAMPLE_ROLE_OPTIONS.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleSampleRole(role)}
                    className="text-xs rounded-full border border-border bg-surface px-3 py-1.5 font-medium text-surface-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="job-description-input" className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Briefcase className="w-4 h-4 text-primary" />
                Job Description
              </label>
              <button
                type="button"
                onClick={handleGenerateSampleJob}
                disabled={isGeneratingSampleJob || !resumeText.trim()}
                className="text-xs text-primary hover:text-primary/80 font-semibold transition-colors inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1 disabled:opacity-50 disabled:pointer-events-none"
                aria-label="Generate a job description based on your resume"
              >
                {isGeneratingSampleJob ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5 text-accent" />
                    Generate one from my resume
                  </>
                )}
              </button>
            </div>
            {jobView === "preview" ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    focusJobOnEdit.current = true;
                    setJobView("edit");
                  }}
                  className="absolute top-2 right-4 z-10 inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-primary hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
                <div
                  id="job-description-preview"
                  data-testid="job-description-preview"
                  className="prose prose-sm max-w-none h-64 overflow-y-auto rounded-lg border border-border bg-surface px-4 py-3"
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{jobDescription}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <textarea
                id="job-description-input"
                ref={jobTextareaRef}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                onFocus={() => {
                  if (!hasTrackedJobFocus.current) {
                    hasTrackedJobFocus.current = true;
                    trackEvent("resume_check_jd_focused");
                  }
                }}
                onBlur={(e) => {
                  if (looksLikeMarkdown(e.target.value)) setJobView("preview");
                }}
                placeholder="Paste the job description here."
                rows={10}
                className="input-thin w-full resize-none"
                aria-describedby="jd-hint"
              />
            )}
            <p id="jd-hint" className="text-xs text-muted-foreground mt-1.5 px-1">
              Paste the posting, or generate one from your resume.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="pt-2">
            <button
              id="analyze-resume-btn"
              type="submit"
              disabled={!canAnalyze}
              className="btn-primary btn-lg w-full disabled:opacity-50 disabled:pointer-events-none"
            >
              Analyze My Resume
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
